import * as THREE from 'three';
import type { CollisionWorld } from '@/world/collision';
import { boxOverlapsWorld, VEHICLE, type Vehicle } from '@/gameplay/Vehicle';

/**
 * Eén voertuigstap voor alles wat rijdt.
 *
 * Hiervoor stond deze integratie drie keer los in de code — in `Vehicle.#drivingStep`, in
 * `TrafficController.#updateRouteActor` en in `ChaseSystem.#updateVehicleChaser` — en alle
 * drie deden hetzelfde: bereken waar je uitkomt, toets de hele doos, en zet bij een treffer
 * de snelheid op nul. Dat leverde twee klachten op die je meteen voelt.
 *
 * **Je gleed nergens langs.** Schamp je een lantaarnpaal, dan sta je stil in plaats van
 * ernaast door te schuren. Te voet was dat allang opgelost: `CollisionWorld.moveCircle` lost
 * per as op, zodat je langs een muur verder glijdt. Auto's kregen die behandeling nooit. Deze
 * functie doet hetzelfde per as: lukt de volle stap niet, dan wordt eerst alleen X en daarna
 * alleen Z geprobeerd. Blokkeert X terwijl Z vrij is, dan schuur je erlangs.
 *
 * **AI-auto's kwamen nooit meer op gang.** Een auto die tegen iets aan stond zette zijn
 * snelheid op nul, accelereerde de volgende stap weer, raakte hetzelfde obstakel, en bleef zo
 * eeuwig hangen. Met glijden lost het gros daarvan zichzelf op; voor een frontale botsing
 * meldt `blocked` dat de beller de koers moet bijstellen in plaats van blind door te duwen.
 */

export interface VehicleAdvance {
  /** Werkelijk afgelegde afstand langs de rijrichting. */
  readonly moved: number;
  /** Is de rechte stap ergens tegengehouden? */
  readonly blocked: boolean;
  /** Is er langs een obstakel geschuurd in plaats van stilgevallen? */
  readonly slid: boolean;
}

const scratchForward = new THREE.Vector2();

/**
 * Verplaats `body` over `distance` meter langs zijn eigen rijrichting.
 *
 * De stap wordt opgeknipt in stukjes van hoogstens `VEHICLE.maxStepDistance`, zodat een auto
 * op topsnelheid niet door een dunne muur heen schiet en het gedrag niet stilzwijgend van de
 * framerate afhangt — dezelfde reden waarom `moveActor` in `world/bodies.ts` dat voor
 * personages doet.
 */
export function advanceVehicle(
  body: { readonly position: THREE.Vector2; readonly rotation: number },
  distance: number,
  halfWidth: number,
  halfLength: number,
  world: CollisionWorld,
  blockers: readonly Vehicle[],
): VehicleAdvance {
  if (distance === 0) return { moved: 0, blocked: false, slid: false };

  const forward = scratchForward.set(Math.sin(body.rotation), Math.cos(body.rotation));
  const steps = Math.max(1, Math.ceil(Math.abs(distance) / VEHICLE.maxStepDistance));
  const stepDistance = distance / steps;
  const stepX = forward.x * stepDistance;
  const stepZ = forward.y * stepDistance;

  let moved = 0;
  let blocked = false;
  let slid = false;

  const free = (x: number, z: number): boolean => !boxOverlapsWorld(
    x,
    z,
    halfWidth,
    halfLength,
    world,
    blockers,
    body.rotation,
  );

  for (let step = 0; step < steps; step++) {
    const { x, y: z } = body.position;

    if (free(x + stepX, z + stepZ)) {
      body.position.set(x + stepX, z + stepZ);
      moved += stepDistance;
      continue;
    }

    blocked = true;

    // Per as uitwijken: de component die vrij is mag door, de geblokkeerde valt weg. Dat is
    // precies wat langs een muur schuren is.
    if (stepX !== 0 && free(x + stepX, z)) {
      body.position.set(x + stepX, z);
      slid = true;
      continue;
    }
    if (stepZ !== 0 && free(x, z + stepZ)) {
      body.position.set(x, z + stepZ);
      slid = true;
      continue;
    }

    break;
  }

  return { moved, blocked, slid };
}
